import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Modal, ModalText, ModalTitle, StyledModalRemixIcon } from '@components/modals/Modal';
import { RiNotification2Line } from '@remixicon/react';
import { EditTicketType } from '@type/EditionTypes';
import { Form, FormGroup, FormInput, FormLabel, FormCheckbox, FormFieldset } from '@eventsquare/uikit/forms';
import { Button, Message } from '@eventsquare/uikit';
import { useCallback, useMemo, useState } from 'react';
import { api } from '@lib/api';
import { useAppSelector } from '@redux/hooks';
import { useParams } from 'react-router-dom';

type SubscribeModalProps = {
    open: boolean;
    type?: EditTicketType | undefined;
    closeModal: () => void;
};

type SubscribeModalValues = {
    email: string;
    newsletter: boolean;
};

export const SubscribeModal: React.FC<SubscribeModalProps> = (props) => {
    const { closeModal, open, type } = props;
    const { edition, event } = useAppSelector((state) => {
        return { edition: state.store.edition, event: state.store.event };
    });

    const { t } = useTranslation();
    const { lang } = useParams();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);

    const initialValues: SubscribeModalValues = useMemo(() => {
        return {
            email: '',
            newsletter: false,
        };
    }, []);

    const yupSchema = yup.object().shape({
        email: yup
            .string()
            .required(t('modal.waiting_list.form.email_required'))
            .email(t('modal.waiting_list.form.email_invalid')),
        newsletter: yup.boolean(),
    });

    const handleSubmit = useCallback(
        async (values: SubscribeModalValues) => {
            setSuccess(false);
            setError(false);

            const subscriptionData = {
                edition_id: edition?.id,
                channel_id: edition?.channel?.id,
                customer: {
                    email: values.email,
                    language_code: lang,
                },
                newsletter: values.newsletter,
                type_id: type?.id,
            };

            setLoading(true);
            try {
                await api.post(`/web/subscribers`, subscriptionData);
                setSuccess(true);
            } catch (error) {
                console.error('Error subscribing:', error);
                setError(true);
                // Handle error appropriately, e.g., show a notification or alert
            } finally {
                setLoading(false);
            }
        },
        [edition?.channel?.id, edition?.id, lang, type?.id],
    );

    const handleClose = useCallback(() => {
        setSuccess(false);
        setError(false);
        closeModal();
    }, [closeModal]);

    if (success) {
        return (
            <Modal closeModal={handleClose} isVisible={open} center>
                <StyledModalRemixIcon>
                    <RiNotification2Line />
                </StyledModalRemixIcon>
                <ModalTitle>{t('modal.waiting_list.success.title')}</ModalTitle>
                <ModalText>{t('modal.waiting_list.success.lead')}</ModalText>
                <Button type="button" onClick={handleClose} block>
                    {t('modal.waiting_list.success.close')}
                </Button>
            </Modal>
        );
    }

    return (
        <Modal closeModal={handleClose} isVisible={open} center>
            <StyledModalRemixIcon>
                <RiNotification2Line />
            </StyledModalRemixIcon>
            <ModalTitle>{t('modal.waiting_list.form.title')}</ModalTitle>
            <ModalText>{t('modal.waiting_list.form.lead')}</ModalText>
            {error && <Message alert type="error" message={t('modal.waiting_list.form.error')} />}
            <Form initialValues={initialValues} validationSchema={yupSchema} onSubmit={handleSubmit}>
                <FormFieldset disabled={loading}>
                    <FormGroup>
                        <FormLabel htmlFor="email">{t('modal.waiting_list.form.email')}</FormLabel>
                        <FormInput autoComplete="email" name="email" type="email" />
                    </FormGroup>
                    <FormGroup>
                        <FormCheckbox
                            label={t('modal.waiting_list.form.newsletter', {
                                organisationName:
                                    event?.organisation?.name || t('modal.waiting_list.form.this_organisation'),
                            })}
                            name="newsletter"
                        />
                    </FormGroup>
                </FormFieldset>
                <Button type="submit" block>
                    {t('modal.waiting_list.form.submit')}
                </Button>
                <Button type="button" variant="link" onClick={handleClose} block>
                    {t('modal.waiting_list.form.cancel')}
                </Button>
            </Form>
        </Modal>
    );
};
